import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router';

import { getAuthMe } from '@/api/generated/auth/auth';
import { AnimatedFullScreenLoader } from '@/components/ui/animated-full-screen-loader';
import { hasDeviceSession } from '@/config/auth/deviceToken';
import { useOffice } from '@/config/office';
import { queryKeys } from '@/config/query/queryKeys';

import { ROUTES } from '../../utils/routeUtils';

interface ProtectedRouteProps {
  redirectPath?: string;
}

export function ProtectedRoute({ redirectPath = ROUTES.LOGIN }: ProtectedRouteProps) {
  const queryClient = useQueryClient();
  const signedIn = hasDeviceSession();
  const {
    error: officeError,
    isInitialized: isOfficeReady,
    isLoading: isOfficeLoading,
  } = useOffice();

  // Connection state gate: /auth/me reports whether a mailbox is connected and
  // whether its Graph credential is still active. 401/403 handling (sign-out,
  // forbidden routing) lives in the mutator.
  const { data: me, isLoading: isMeLoading } = useQuery({
    enabled: signedIn,
    queryFn: () => getAuthMe(),
    queryKey: queryKeys.auth.session(),
    staleTime: 30_000,
  });

  // Seed the shared user cache so consumers (mismatch check, Intercom,
  // profile) have the profile before useRefreshMe mounts inside /home.
  useEffect(() => {
    if (me?.user) {
      queryClient.setQueryData(queryKeys.auth.user(), me.user);
    }
  }, [me?.user, queryClient]);

  // No device session — go sign in.
  if (!signedIn) {
    return <Navigate replace to={redirectPath} />;
  }

  // Wait for Office.js and the connection probe.
  if (isOfficeLoading || isMeLoading) {
    return <AnimatedFullScreenLoader show={true} />;
  }

  // Office.js failed or is not ready — getUserEmail() would return null,
  // making mailbox-context features impossible; redirect to service-unavailable
  if (!isOfficeReady || officeError) {
    return <Navigate replace to={ROUTES.SERVICE_UNAVAILABLE} />;
  }

  // Signed in but no connected mailbox (or a revoked Graph credential) —
  // the Connect Mailbox screen handles both connect and reconnect.
  if (me && (!me.mailboxConnected || !me.graphTokenActive)) {
    return <Navigate replace to={ROUTES.CONNECT_MAILBOX} />;
  }

  return <Outlet />;
}
